import { useEffect, useState } from "react";
import { Check, RotateCcw, Mail } from "lucide-react";
import {
  listSupportMessages,
  updateSupportMessage,
  type SupportMessage,
  type SupportStatus,
} from "../../../features/support/supportApi";
import { PageHeader, shortDate } from "./_shared";

const CATEGORY_TONE: Record<string, string> = {
  billing: "var(--gold-600,#7c5f2e)",
  technical: "var(--accent-strong,#a0793d)",
  bug: "var(--red,#c2564e)",
  sales: "var(--emerald,#2e7d4f)",
  general: "var(--ink-500,#95897a)",
};

export default function AdminSupport() {
  const [items, setItems] = useState<SupportMessage[] | null>(null);
  const [filter, setFilter] = useState<SupportStatus | "all">("open");

  const load = () =>
    listSupportMessages(filter === "all" ? undefined : filter)
      .then(setItems)
      .catch(() => setItems([]));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const setStatus = async (m: SupportMessage, status: SupportStatus) => {
    await updateSupportMessage(m._id, status);
    await load();
  };

  const openCount = (items ?? []).filter((m) => m.status === "open").length;

  return (
    <>
      <PageHeader
        title="Support inbox"
        subtitle="Contact-form and in-app help messages from users"
        actions={
          <div className="flex items-center gap-0.5 rounded-full border border-outline/40 bg-surface-container-low p-0.5">
            {(["open", "resolved", "all"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={
                  "rounded-full px-3 py-1.5 text-[12.5px] font-medium capitalize transition " +
                  (filter === f ? "bg-surface text-on-surface shadow-sm" : "text-on-surface-variant hover:text-on-surface")
                }
              >
                {f}
                {f === "open" && filter !== "open" && openCount > 0 ? ` · ${openCount}` : ""}
              </button>
            ))}
          </div>
        }
      />
      <div className="p-4 sm:p-6">
        {items === null ? (
          <div className="py-16 text-center text-[13px] text-on-surface-variant">Loading messages…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-outline/40 py-16 text-center text-[13px] text-on-surface-variant">
            No {filter === "all" ? "" : filter} messages.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((m) => (
              <div key={m._id} className="rounded-2xl border border-outline/30 bg-surface p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[14px] font-semibold text-on-surface">{m.name}</span>
                  <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1 text-[12.5px] text-on-surface-variant hover:text-on-surface">
                    <Mail size={12} /> {m.email}
                  </a>
                  <span
                    className="rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-white"
                    style={{ background: CATEGORY_TONE[m.category] ?? CATEGORY_TONE.general }}
                  >
                    {m.category}
                  </span>
                  {m.status === "resolved" && (
                    <span className="rounded-full bg-emerald/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-emerald">
                      Resolved
                    </span>
                  )}
                  <span className="ml-auto text-[12px] text-on-surface-variant">{shortDate(m.createdAt)}</span>
                </div>
                {m.subject && <div className="mt-2 text-[13.5px] font-medium text-on-surface">{m.subject}</div>}
                <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-on-surface-variant">{m.message}</p>
                <div className="mt-3 flex justify-end gap-2">
                  {m.status === "open" ? (
                    <button
                      onClick={() => setStatus(m, "resolved")}
                      className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-[12.5px] font-semibold text-on-primary hover:opacity-90"
                    >
                      <Check size={13} /> Mark resolved
                    </button>
                  ) : (
                    <button
                      onClick={() => setStatus(m, "open")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-outline/40 px-3.5 py-1.5 text-[12.5px] font-medium text-on-surface-variant hover:text-on-surface"
                    >
                      <RotateCcw size={13} /> Reopen
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
